import React from "react";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { GrainDefs, GrainOverlay } from "./Grain";
import { useReducedMotion } from "../motion";

const ID = "mentordesk";
const W = 600;
const H = 440;
const DESK_TOP = 330; // torsos tuck behind this
const FLOOR = 402;
const c = palette.ill;

// A seated figure in flat shapes: a hair disc with the face disc nudged down
// over it, and a torso that runs behind the desk edge.
const Figure = ({ x, headY, r, shirt, hair, skin, longHair }) => {
  const shoulder = headY + r + 10;
  const halfWidth = r * 1.5;
  return (
    <g>
      {longHair && (
        <rect
          x={x - r * 1.14}
          y={headY - r * 0.4}
          width={r * 2.28}
          height={r * 2.4}
          rx={r * 0.95}
          fill={hair}
        />
      )}
      <path
        d={`M${x - halfWidth} ${DESK_TOP + 4} Q${x - halfWidth} ${shoulder} ${x} ${shoulder} Q${x + halfWidth} ${shoulder} ${x + halfWidth} ${DESK_TOP + 4} Z`}
        fill={shirt}
      />
      <circle cx={x} cy={headY - 5} r={r * 1.13} fill={hair} />
      <circle cx={x} cy={headY} r={r} fill={skin} />
    </g>
  );
};

export const MentorDesk = ({ className }) => {
  const reduced = useReducedMotion();

  const float = (distance, duration, delay = 0) =>
    reduced
      ? {}
      : {
          animate: { y: [0, -distance, 0] },
          transition: { duration, repeat: Infinity, ease: "easeInOut", delay },
        };

  return (
    <motion.svg
      className={className}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label="A mentor and a learner sitting together at a desk, working through a problem on a laptop"
      initial={reduced ? false : { opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <defs>
        <GrainDefs id={ID} />
        <clipPath id={`${ID}-panel`}>
          <rect width={W} height={H} rx="28" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${ID}-panel)`}>
        <rect width={W} height={H} fill={c.sky} />
        <rect y={FLOOR} width={W} height={H - FLOOR} fill={c.sand} />

        {/* Sun, breathing slowly */}
        <motion.circle
          cx="506"
          cy="84"
          r="46"
          fill={c.sun}
          {...(reduced
            ? {}
            : {
                animate: { opacity: [0.85, 1, 0.85], scale: [1, 1.04, 1] },
                transition: { duration: 7, repeat: Infinity, ease: "easeInOut" },
              })}
          style={{ transformOrigin: "506px 84px" }}
        />

        {/* Clouds */}
        <motion.g fill="#ffffff" opacity="0.85" {...float(4, 11)}>
          <rect x="78" y="64" width="88" height="17" rx="8.5" />
          <rect x="102" y="49" width="50" height="16" rx="8" />
        </motion.g>
        <motion.g fill="#ffffff" opacity="0.65" {...float(3, 13, 1.5)}>
          <rect x="336" y="104" width="62" height="14" rx="7" />
        </motion.g>

        {/* Flow arcs — a quiet nod to the name, drifting behind the scene */}
        {[0, 1, 2].map((i) => (
          <motion.path
            key={i}
            d={`M-40 ${180 + i * 28} Q 150 ${152 + i * 28} 300 ${180 + i * 28} T 640 ${180 + i * 28}`}
            fill="none"
            stroke={c.river}
            strokeWidth="2.5"
            strokeLinecap="round"
            opacity={0.14}
            strokeDasharray="14 22"
            {...(reduced
              ? {}
              : {
                  animate: { strokeDashoffset: [0, -72] },
                  transition: {
                    duration: 9 + i * 2,
                    repeat: Infinity,
                    ease: "linear",
                  },
                })}
          />
        ))}

        {/* Plant, standing on the floor */}
        <g>
          <ellipse cx="54" cy="300" rx="16" ry="30" fill={c.leafDeep} transform="rotate(-24 54 300)" />
          <ellipse cx="82" cy="294" rx="15" ry="29" fill={c.leaf} transform="rotate(18 82 294)" />
          <ellipse cx="68" cy="280" rx="13" ry="26" fill={c.leafDeep} opacity="0.8" />
          <path d={`M46 344 H94 L86 ${FLOOR} H54 Z`} fill={c.clay} />
          <rect x="42" y="337" width="56" height="12" rx="6" fill={c.claySoft} />
        </g>

        {/* Figures first, so the desk overlaps them */}
        <Figure x={244} headY={182} r={34} shirt={c.plum} hair={c.ink} skin={c.skin[2]} />
        <Figure x={376} headY={194} r={31} shirt={c.leaf} hair="#3a2b26" skin={c.skin[0]} longHair />

        {/* Mentor gestures at the screen; learner's hand rests on the laptop */}
        <path
          d="M288 246 Q 314 254 326 286"
          fill="none"
          stroke={c.skin[2]}
          strokeWidth="12"
          strokeLinecap="round"
        />
        <path
          d="M340 256 Q 330 286 342 306"
          fill="none"
          stroke={c.skin[0]}
          strokeWidth="11"
          strokeLinecap="round"
        />

        {/* Laptop, sitting on the desk in front of the learner */}
        <g>
          <rect x="306" y="258" width="62" height="54" rx="5" fill={palette.paper} stroke={c.ink} strokeWidth="3.5" />
          <rect x="317" y="270" width="34" height="4" rx="2" fill={c.river} />
          <rect x="317" y="280" width="24" height="4" rx="2" fill={c.skyDeep} />
          <rect x="317" y="290" width="29" height="4" rx="2" fill={c.skyDeep} />
          <rect x="296" y="311" width="82" height="12" rx="6" fill={c.ink} />
        </g>

        {/* Desk */}
        <rect x="108" y={DESK_TOP} width="384" height="16" rx="8" fill={c.wood} />
        <rect x="150" y={DESK_TOP + 16} width="13" height={FLOOR - DESK_TOP - 16} rx="3" fill={c.woodDeep} />
        <rect x="437" y={DESK_TOP + 16} width="13" height={FLOOR - DESK_TOP - 16} rx="3" fill={c.woodDeep} />
        <rect x="150" y={FLOOR - 22} width="300" height="9" rx="4" fill={c.woodDeep} opacity="0.75" />

        {/* Floating cards — the product peeking into the scene */}
        <motion.g {...float(9, 6)}>
          <rect x="88" y="98" width="118" height="76" rx="12" fill={palette.paper} opacity="0.96" />
          <rect x="104" y="116" width="42" height="7" rx="3.5" fill={c.river} />
          <rect x="104" y="132" width="76" height="5" rx="2.5" fill={c.skyDeep} />
          <rect x="104" y="144" width="60" height="5" rx="2.5" fill={c.skyDeep} />
          <rect x="104" y="156" width="70" height="5" rx="2.5" fill={c.skyDeep} />
        </motion.g>

        <motion.g {...float(7, 7.5, 0.8)}>
          <rect x="404" y="150" width="108" height="74" rx="12" fill={palette.paper} opacity="0.96" />
          <rect x="420" y="164" width="36" height="6" rx="3" fill={c.sand} />
          <rect x="420" y="198" width="17" height="14" rx="3" fill={c.skyDeep} />
          <rect x="444" y="186" width="17" height="26" rx="3" fill={c.river} />
          <rect x="468" y="178" width="17" height="34" rx="3" fill={c.plum} />
        </motion.g>

        <GrainOverlay id={ID} width={W} height={H} rx={28} />
      </g>
    </motion.svg>
  );
};

export default MentorDesk;
